<template>
  <v-container fluid class="insure-apply-view fill-height pa-0">
    <v-row no-gutters class="fill-height">
      <!-- Left Panel: Insure Form -->
      <v-col cols="4" class="fill-height white border-r d-flex flex-column">
        <div class="insure-apply-header px-4 py-2 d-flex align-center justify-space-between flex-shrink-0">
          <div class="d-flex align-center">
            <div class="text-h6 font-weight-medium">投保申请</div>
            <v-chip x-small class="ml-2" :color="workMode === 'draw' ? 'primary' : 'success'" text-color="white">
              {{ workMode === 'draw' ? '画地块模式' : '投保模式' }}
            </v-chip>
          </div>
          <div class="text-body-2 grey--text">{{ currentAreaName || '未选择辖区' }}</div>
        </div>
        <div class="pa-4 flex-grow-1 d-flex flex-column" style="min-height: 0;">
          <div class="flex-grow-1 overflow-y-auto">
            <wgu-insuretool-win v-if="workMode === 'insure'" :visible="true" :closable="false" :minimizable="false"
              :draggable="false" :embedded="true" :showToolbar="false" :insureTypeOverride="'searchPlot'" win="sidebar"
              @request-draw-mode="switchWorkMode('draw')" @request-search-mode="switchWorkMode('insure')" />
            <ks-plot-draw-panel v-else :map-ref="mapRef" :map-show-insured-plots="showInsuredPlots"
              :map-show-uninsured-plots="showUninsuredPlots" :map-show-boundary-only="showBoundaryOnly"
              @request-search-mode="switchWorkMode('insure')" @plots-changed="handlePlotsChanged" />
          </div>
        </div>
      </v-col>

      <!-- Right Panel: Map -->
      <v-col cols="8" class="fill-height grey lighten-5 map-col">
        <div id="insure-apply-ol-container" ref="mapContainer" class="fill-height map-container">
          <wgu-map target="insure-apply-ol-container" />
          <wgu-maploading-status />
          <wgu-app-logo />
          <wgu-bglayerswitcher :force-show="true" />
          <wgu-overviewmap v-if="overviewMapConfig" v-bind="overviewMapConfig" />
          <wgu-hover-tooltip />
          <ks-popup-tooltip />
        </div>
        <div class="insure-status-filter">
          <v-btn x-small :outlined="!showInsuredPlots" :color="showInsuredPlots ? 'warning' : 'grey'" class="mr-1"
            @click="togglePlotStatus('insured')">
            已投保
          </v-btn>
          <v-btn x-small :outlined="!showUninsuredPlots" :color="showUninsuredPlots ? 'success' : 'grey'"
            class="mr-1"
            @click="togglePlotStatus('uninsured')">
            未投保
          </v-btn>
          <v-btn x-small :outlined="!showBoundaryOnly" :color="showBoundaryOnly ? 'primary' : 'grey'"
            @click="togglePlotStatus('boundary')">
            地块边界
          </v-btn>
        </div>
        <div class="insure-map-toolbar">
          <v-btn icon small class="mr-1" :title="'放大'" @click="zoomIn">
            <v-icon>mdi-plus</v-icon>
          </v-btn>
          <v-btn icon small class="mr-1" :title="'缩小'" @click="zoomOut">
            <v-icon>mdi-minus</v-icon>
          </v-btn>
          <v-btn icon small :title="'刷新'" @click="refreshMap">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </div>
      </v-col>
    </v-row>
    <v-snackbar v-model="snackbar" top>
      {{ snackbarText }}
    </v-snackbar>
  </v-container>
</template>

<script>
import Vue from 'vue'
import OlMap from '@/components/ol/Map'
import HoverTooltip from '@/components/ol/HoverTooltip'
import PopUpTooltip from '@/components/ol/PopUpTooltip'
import AppLogo from '@/components/AppLogo'
import BgLayerSwitcher from '@/components/bglayerswitcher/BgLayerSwitcher.vue'
import OverviewMap from '@/components/overviewmap/OverviewMap.vue'
import MapLoadingStatus from '@/components/progress/MapLoadingStatus'
import InsureWin from '@/components/insuretool/InsureWin.vue'
import PlotDrawPanel from '@/components/plotdraw/PlotDrawPanel.vue'
import AutoLocateArea from '@/mixins/AutoLocateArea'
import UnifiedMapLifecycle from '@/mixins/UnifiedMapLifecycle'
import { mapState } from 'vuex'
import { useInsureApplyStore } from '@/stores/insureApply'
import { Style, Fill, Stroke } from 'ol/style'
import { transformExtent } from 'ol/proj'
import { unByKey } from 'ol/Observable'
import { PLOT_WMS_URL, PLOT_WMS_LAYER, PLOT_WMS_STYLES } from '@/const/const.js'
import { buildPlotStatusCql } from '@/util/plotFilters'
import { buildPlotWmsSldBody } from '@/util/PlotWmsSld'

const UNINSURED_WATER_STYLE = new Style({
  fill: new Fill({ color: 'rgba(13, 71, 161, 0.35)' }),
  stroke: new Stroke({ color: '#0D47A1', width: 2 })
})

const UNINSURED_DEFAULT_STYLE = new Style({
  fill: new Fill({ color: 'rgba(102, 187, 106, 0.35)' }),
  stroke: new Stroke({ color: '#388E3C', width: 2 })
})

export default {
  name: 'InsureApplyView',
  mixins: [AutoLocateArea, UnifiedMapLifecycle],
  components: {
    'wgu-map': OlMap,
    'wgu-hover-tooltip': HoverTooltip,
    'wgu-app-logo': AppLogo,
    'wgu-bglayerswitcher': BgLayerSwitcher,
    'wgu-overviewmap': OverviewMap,
    'wgu-maploading-status': MapLoadingStatus,
    'wgu-insuretool-win': InsureWin,
    'ks-plot-draw-panel': PlotDrawPanel,
    'ks-popup-tooltip': PopUpTooltip
  },
  data () {
    // 投保申请页面的图层配置 - 包含所有地块相关图层
    const mapLayersConfig = [
      {
        type: 'IMAGEWMS',
        lid: 'region-layer',
        name: '行政区划',
        ratio: 1.5,
        format: 'image/png',
        layers: 'ks:geo_district_code',
        url: 'https://geo.kungsong.com/geoserver/ks/wms',
        transparent: true,
        projection: 'EPSG:4326',
        attribution: 'provided by kungsong.com',
        isBaseLayer: false,
        visible: true,
        displayInLayerList: true,
        opacityControl: true,
        crossOrigin: 'anonymous'
      },
      {
        type: 'IMAGEWMS',
        lid: 'wms-plot-layer',
        name: '地块信息 (WMS)',
        url: PLOT_WMS_URL,
        layers: PLOT_WMS_LAYER,
        format: 'image/png',
        transparent: true,
        projection: 'EPSG:4326',
        visible: true,
        isBaseLayer: false,
        displayInLayerList: true,
        opacityControl: true,
        crossOrigin: 'anonymous'
      },
      {
        type: 'VECTOR',
        lid: 'insured-plots-layer',
        name: '已投保地块',
        url: 'plots/?bs=1',
        format: 'GeoJSON',
        visible: false,
        selectable: true,
        hoverable: true,
        hoverAttribute: 'qsrmc',
        displayInLayerList: true,
        opacity: 0.8,
        style: {
          strokeColor: '#FBC02D',
          strokeWidth: 2,
          fillColor: 'rgba(253, 216, 53, 0.45)'
        },
        selectStyle: {
          strokeColor: '#d32f2f',
          strokeWidth: 3,
          fillColor: 'rgba(211, 47, 47, 0.4)'
        }
      },
      {
        type: 'VECTOR',
        lid: 'uninsured-plots-layer',
        name: '未投保地块',
        url: 'plots/?bs=0',
        format: 'GeoJSON',
        visible: false,
        selectable: true,
        hoverable: true,
        hoverAttribute: 'qsrmc',
        displayInLayerList: true,
        opacity: 0.6,
        style: {
          strokeColor: '#388E3C',
          strokeWidth: 2,
          fillColor: 'rgba(102, 187, 106, 0.35)'
        },
        selectStyle: {
          strokeColor: '#d32f2f',
          strokeWidth: 3,
          fillColor: 'rgba(211, 47, 47, 0.4)'
        }
      },
      {
        type: 'VECTOR',
        lid: 'plot-boundaries-layer',
        name: '地块边界',
        url: 'plots/',
        format: 'GeoJSON',
        visible: false,
        selectable: false,
        hoverable: false,
        displayInLayerList: true,
        opacity: 1.0,
        style: {
          strokeColor: '#388E3C',
          strokeWidth: 1,
          fillColor: 'rgba(0, 0, 0, 0)'
        },
        zIndex: 6
      },
      {
        type: 'XYZ',
        url: 'https://t4.tianditu.gov.cn/DataServer?T=cia_w&tk=2d986e569889fd35b5587da2ce9574b7&x={x}&y={y}&l={z}',
        lid: 'satelliteMark',
        name: '天地图标注',
        isBaseLayer: false,
        visible: true,
        crossOrigin: 'anonymous'
      },
      {
        type: 'XYZ',
        url: 'https://t6.tianditu.gov.cn/DataServer?T=img_w&x={x}&y={y}&l={z}&tk=2d986e569889fd35b5587da2ce9574b7',
        lid: 'satelliteBackground',
        name: '天地图卫星',
        isBaseLayer: true,
        visible: true,
        crossOrigin: 'anonymous'
      },
      {
        type: 'OSM',
        lid: 'osm-bg',
        name: 'OpenStreetMap',
        isBaseLayer: true,
        visible: false,
        crossOrigin: 'anonymous'
      }
    ];

    return {
      mapLayersConfig,
      overviewMapConfig: this.getOverviewMapConfig(),
      mapRef: null,
      snackbar: false,
      snackbarText: '',
      workMode: 'insure',
      showInsuredPlots: true,
      showUninsuredPlots: true,
      showBoundaryOnly: false,
      mapMoveEndKey: null,
      pendingVectorRefreshTimer: null
    }
  },
  computed: {
    ...mapState(['authorizedArea']),
    insureApplyStore () {
      return useInsureApplyStore()
    },
    currentAreaName () {
      return this.$store?.state?.selectedArea?.display_name || this.$store?.state?.selectedArea?.name || ''
    },
    selectedArea () {
      return this.$store?.state?.selectedArea || null
    }
  },
  watch: {
    selectedArea (newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.updateWmsLayerParams()
        this.autoLocateToSelectedArea()
        this.scheduleVectorLayerRefresh(true)
      }
    },
    authorizedArea: {
      handler (val) {
        this.updatePlotLayersUrl()
      },
      deep: true
    },
    '$route.query.mode' (newVal) {
      this.workMode = this.normalizeWorkMode(newVal)
    }
  },
  created () {
    const persistedBoundaryOnly = this.insureApplyStore?.mapUiState?.boundaryOnly
    if (typeof persistedBoundaryOnly === 'boolean') {
      this.showBoundaryOnly = persistedBoundaryOnly
    }
    this.workMode = this.normalizeWorkMode(this.$route?.query?.mode)
    Vue.prototype.$appConfig.mapLayers = this.mapLayersConfig
  },
  async mounted () {
    // 先加载授权区域数据
    try {
      await this.$store.dispatch('fetchAuthRegions')
      console.log('InsureApplyView - Authorized area loaded:', this.authorizedArea)
    } catch (error) {
      console.error('Failed to load authorized area:', error)
    }

    this.startUnifiedMapLifecycle({
      containerRef: 'mapContainer',
      onMapReady: (map) => {
        this.mapRef = map
        this.registerMapMoveEndListener()
        this.showPlotLayers()
        this.updateWmsLayerParams()
        this.autoLocateToSelectedArea()
        this.scheduleVectorLayerRefresh()
      },
      extraDelays: [200, 800]
    })
  },
  beforeDestroy () {
    if (this.mapMoveEndKey) {
      unByKey(this.mapMoveEndKey)
      this.mapMoveEndKey = null
    }
    if (this.pendingVectorRefreshTimer) {
      clearTimeout(this.pendingVectorRefreshTimer)
      this.pendingVectorRefreshTimer = null
    }
    this.stopUnifiedMapLifecycle()
  },
  methods: {
    getOverviewMapConfig () {
      const appConfig = Vue.prototype.$appConfig || {}
      const overviewMapConf = appConfig.overviewMap
      if (overviewMapConf) {
        return { ...overviewMapConf, visible: false }
      }
      return { visible: false }
    },
    getVillageCodesForFilter () {
      const selectedCode = this.selectedArea?.place_id || this.selectedArea?.code
      if (selectedCode) {
        return [selectedCode]
      }

      const boundCode = this.$store?.state?.user?.bound_region_code
      if (boundCode) {
        return [boundCode]
      }

      const codes = []
      if (this.authorizedArea && this.authorizedArea.length > 0) {
        this.authorizedArea.forEach(auth => {
          const code = auth.place_id || auth.code
          if (code) {
            codes.push(code)
          }
        })
      }

      return [...new Set(codes)]
    },
    registerMapMoveEndListener () {
      if (!this.mapRef || this.mapMoveEndKey) return
      this.mapMoveEndKey = this.mapRef.on('moveend', () => {
        this.updateVectorLayerUrls()
      })
    },
    scheduleVectorLayerRefresh (forceRefresh = false, delay = 900) {
      if (this.pendingVectorRefreshTimer) {
        clearTimeout(this.pendingVectorRefreshTimer)
      }
      this.pendingVectorRefreshTimer = setTimeout(() => {
        this.pendingVectorRefreshTimer = null
        this.updateVectorLayerUrls(forceRefresh)
      }, delay)
    },
    getCurrentViewBbox () {
      if (!this.mapRef) return null

      const size = this.mapRef.getSize()
      const view = this.mapRef.getView()
      if (!size || !view) return null

      const rawExtent = view.calculateExtent(size)
      if (!Array.isArray(rawExtent) || rawExtent.length !== 4) {
        return null
      }

      const projection = view.getProjection()
      const extent4326 = projection?.getCode?.() === 'EPSG:4326'
        ? rawExtent
        : transformExtent(rawExtent, projection, 'EPSG:4326')

      const [minx, miny, maxx, maxy] = extent4326.map(value => Number(Number(value).toFixed(6)))
      if (![minx, miny, maxx, maxy].every(Number.isFinite) || maxx <= minx || maxy <= miny) {
        return null
      }

      return { minx, miny, maxx, maxy }
    },
    updatePlotLayersUrl (forceRefresh = false) {
      this.updateVectorLayerUrls(forceRefresh)
      this.updateWmsLayerParams()
    },
    updateVectorLayerUrls (forceRefresh = false) {
      if (!this.mapRef) {
        console.warn('updateVectorLayerUrls: Map not ready')
        return
      }

      const layers = this.mapRef.getLayers().getArray()
      const vectorLayerIds = ['insured-plots-layer', 'uninsured-plots-layer', 'plot-boundaries-layer']
      const villageCodes = this.getVillageCodesForFilter()
      const bbox = this.getCurrentViewBbox()

      console.log('InsureApplyView - updateVectorLayerUrls called')
      console.log('- authorizedArea:', this.authorizedArea)
      console.log('- selectedArea:', this.selectedArea)
      console.log('- villageCodes:', villageCodes)
      console.log('- bbox:', bbox)

      if (villageCodes.length === 0) {
        console.warn('No village codes found, skipping plot filter')
        return
      }

      if (!bbox) {
        console.warn('No current map bbox found, skipping vector refresh')
        return
      }

      // Update Vector Layers
      vectorLayerIds.forEach(lid => {
        const layer = layers.find(l => l.get('lid') === lid)
        if (layer) {
          const isBoundaryLayer = lid === 'plot-boundaries-layer'
          const shouldShowLayer = isBoundaryLayer || this.workMode !== 'insure'
            ? false
            : (lid === 'insured-plots-layer' ? this.showInsuredPlots : this.showUninsuredPlots)
          layer.setVisible(shouldShowLayer)

          if (lid === 'uninsured-plots-layer') {
            this.applyUninsuredVectorStyle(layer)
          }

          if (!shouldShowLayer) {
            return
          }

          const source = layer.getSource()
          if (source && typeof source.setUrl === 'function' && typeof source.getUrl === 'function') {
            const currentUrl = source.getUrl()

            if (!currentUrl || typeof currentUrl !== 'string') {
              return
            }

            let urlBase = currentUrl
            let queryString = ''

            if (currentUrl.indexOf('?') > -1) {
              const parts = currentUrl.split('?')
              urlBase = parts[0]
              queryString = parts[1]
            }

            const params = new URLSearchParams(queryString)
            params.set('village_code', villageCodes.join(','))
            params.set('minx', String(bbox.minx))
            params.set('miny', String(bbox.miny))
            params.set('maxx', String(bbox.maxx))
            params.set('maxy', String(bbox.maxy))
            params.set('limit', '5000')

            const newUrl = `${urlBase}?${params.toString()}`

            if (currentUrl !== newUrl) {
              source.setUrl(newUrl)
            }

            if (currentUrl !== newUrl || forceRefresh) {
              source.refresh()
            }
          }
        }
      })
    },
    updateWmsLayerParams () {
      if (!this.mapRef) {
        console.warn('updateWmsLayerParams: Map not ready')
        return
      }
      const layers = this.mapRef.getLayers().getArray()
      const wmsLayerIds = ['wms-plot-layer']
      const villageCodes = this.getVillageCodesForFilter()
      if (villageCodes.length === 0) {
        return
      }
      wmsLayerIds.forEach(lid => {
        const layer = layers.find(l => l.get('lid') === lid)
        if (layer) {
          const source = layer.getSource()
          if (source && typeof source.updateParams === 'function') {
            const villageFilter = `village_code IN ('${villageCodes.join("','")}')`
            const statusFilter = this.buildPlotStatusFilter()
            const filter = `${villageFilter} AND ${statusFilter} AND (hidden IS NULL OR hidden <> 1)`
            const styleParams = this.buildPlotWmsStyleParams()
            source.updateParams({
              CQL_FILTER: filter,
              ...styleParams,
              _t: Date.now()
            })
            if (typeof source.refresh === 'function') {
              source.refresh()
            }
            console.log('Applied CQL_FILTER to WMS layer:', filter)
          }
        }
      })
    },
    buildPlotStatusFilter () {
      return buildPlotStatusCql(this.showInsuredPlots, this.showUninsuredPlots)
    },
    buildPlotWmsStyleParams () {
      if (this.showBoundaryOnly) {
        return {
          STYLES: PLOT_WMS_STYLES.MAP_BOUNDARY_ONLY,
          SLD_BODY: buildPlotWmsSldBody(PLOT_WMS_LAYER, {
            styleType: 'single',
            boundaryOnly: true
          })
        }
      }

      return {
        STYLES: PLOT_WMS_STYLES.INSURE_APPLY,
        SLD_BODY: buildPlotWmsSldBody(PLOT_WMS_LAYER, {
          styleType: 'status',
          showInsured: this.showInsuredPlots,
          showUninsured: this.showUninsuredPlots,
          highlightUninsuredWater: true
        })
      }
    },
    applyUninsuredVectorStyle (layer) {
      if (!layer || typeof layer.setStyle !== 'function') return
      layer.setStyle((feature) => {
        const landType = String(feature.get('land_type') ?? feature.get('DL') ?? '').trim()
        if (landType === '42' || landType === '水地') {
          return UNINSURED_WATER_STYLE
        }
        return UNINSURED_DEFAULT_STYLE
      })
    },
    togglePlotStatus (type) {
      if (type === 'insured') {
        this.showInsuredPlots = !this.showInsuredPlots
      }

      if (type === 'uninsured') {
        this.showUninsuredPlots = !this.showUninsuredPlots
      }

      if (type === 'boundary') {
        this.showBoundaryOnly = !this.showBoundaryOnly
        this.insureApplyStore.setBoundaryOnly(this.showBoundaryOnly)
      }

      this.updatePlotLayersUrl(true)
    },
    zoomIn () {
      if (!this.mapRef) return
      const view = this.mapRef.getView()
      view.setZoom((view.getZoom() || 12) + 1)
    },
    zoomOut () {
      if (!this.mapRef) return
      const view = this.mapRef.getView()
      view.setZoom((view.getZoom() || 12) - 1)
    },
    refreshMap () {
      if (!this.mapRef) return
      this.forceRefreshPlotLayers()
      this.mapRef.updateSize()
      this.mapRef.renderSync()
      this.snackbarText = '地图已刷新'
      this.snackbar = true
    },
    forceRefreshPlotLayers () {
      this.updatePlotLayersUrl(true)
    },
    handlePlotsChanged () {
      this.forceRefreshPlotLayers()
    },
    normalizeWorkMode (mode) {
      return mode === 'draw' ? 'draw' : 'insure'
    },
    switchWorkMode (mode) {
      const normalizedMode = this.normalizeWorkMode(mode)
      if (this.workMode === normalizedMode) return

      this.workMode = normalizedMode

      const query = { ...this.$route.query }
      if (normalizedMode === 'draw') {
        query.mode = 'draw'
      } else {
        delete query.mode
      }

      this.$router.replace({ path: '/insure-apply', query }).catch(() => {})

      this.$nextTick(() => {
        this.showPlotLayers()
        if (normalizedMode === 'insure') {
          this.forceRefreshPlotLayers()
        }
      })
    },
    showPlotLayers () {
      if (!this.mapRef) return

      const plotLayerIds = ['region-layer', 'wms-plot-layer']
      const layers = this.mapRef.getLayers().getArray()

      plotLayerIds.forEach(lid => {
        const layer = layers.find(l => l.get('lid') === lid)
        if (layer) {
          layer.setVisible(true)
        }
      })

      const insuredLayer = layers.find(layer => layer.get('lid') === 'insured-plots-layer')
      if (insuredLayer) {
        insuredLayer.setVisible(this.workMode === 'insure' && this.showInsuredPlots)
      }

      const uninsuredLayer = layers.find(layer => layer.get('lid') === 'uninsured-plots-layer')
      if (uninsuredLayer) {
        uninsuredLayer.setVisible(this.workMode === 'insure' && this.showUninsuredPlots)
      }

      const boundaryLayer = layers.find(layer => layer.get('lid') === 'plot-boundaries-layer')
      if (boundaryLayer) {
        boundaryLayer.setVisible(false)
      }
    }
  }
}
</script>

<style scoped>
.insure-apply-view {
  min-height: 100%;
}

.insure-apply-header {
  border-bottom: 1px solid #e0e0e0;
  background: #ffffff;
}

.insure-map-toolbar {
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 6px;
  padding: 6px 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  z-index: 5;
}

.insure-status-filter {
  position: absolute;
  top: 12px;
  left: 12px;
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 6px;
  padding: 6px 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  z-index: 5;
}

.map-col {
  position: relative;
  overflow: hidden;
}

.map-container {
  position: absolute;
  inset: 0;
}
</style>
